import React, { useMemo } from "react";
import { DndContext, DragEndEvent, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useMixboard } from "@/state/useMixboard";
import { MixboardAPI } from "@/lib/mixboard/api";

const debounce = <T extends (...args: any[]) => void>(fn: T, delay = 200) => {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return (...args: Parameters<T>) => {
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => fn(...args), delay);
  };
};

function SortableLayer({
  layer,
  update,
  renderTypographyLayer,
  syncLayer,
  removeLayer,
}: {
  layer: any;
  update: (id: string, patch: Record<string, any>) => void;
  renderTypographyLayer?: (layer: any) => React.ReactNode;
  syncLayer: (id: string, payload: Record<string, any>) => void;
  removeLayer: (id: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: layer.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: layer.hidden ? 0.5 : 1,
  };

  const toggleHidden = () => update(layer.id, { hidden: !layer.hidden });
  const toggleLocked = () => update(layer.id, { locked: !layer.locked });
  const layerData = layer.data ?? {};

  const syncData = (data: Record<string, any>) => {
    update(layer.id, { data });
    syncLayer(layer.id, { data });
  };

  const handleNumberChange = (key: "x" | "y") => (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(event.target.value);
    syncData({ ...layerData, [key]: val });
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    syncData({ ...layerData, text: event.target.value });
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value;
    update(layer.id, { name: val });
    syncData({ ...layerData, name: val });
  };

  const handleFontSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(event.target.value);
    syncData({ ...layerData, fontSize: val });
  };

  const handleRotationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(event.target.value);
    syncData({ ...layerData, rotation: val });
  };

  const handleOpacityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(event.target.value);
    syncData({ ...layerData, opacity: val });
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-muted p-2 rounded space-y-2 border border-transparent ${
        isDragging ? "border-blue-400" : ""
      }`}
      {...attributes}
      {...listeners}
    >
      <div className="flex items-center justify-between">
        <div className="font-semibold text-sm uppercase">{layer.type}</div>
        <div className="space-x-2 text-xs">
          <button
            onClick={toggleHidden}
            className="px-2 py-0.5 rounded bg-muted hover:bg-muted"
          >
            {layer.hidden ? "Show" : "Hide"}
          </button>
          <button
            onClick={toggleLocked}
            className="px-2 py-0.5 rounded bg-muted hover:bg-muted"
          >
            {layer.locked ? "Unlock" : "Lock"}
          </button>
          <button
            onClick={() => removeLayer(layer.id)}
            className="px-2 py-0.5 rounded bg-red-600 hover:bg-red-500 text-foreground"
          >
            Delete
          </button>
        </div>
      </div>
      <div className="flex flex-col">
        <label className="text-xs">Name</label>
        <input
          type="text"
          className="text-black rounded px-2 py-1"
          value={layer.name ?? layerData.name ?? ""}
          disabled={layer.locked}
          onChange={handleNameChange}
        />
      </div>
      <div className="flex flex-col">
        <label className="text-xs">X</label>
        <input
          type="number"
          className="text-black rounded px-2 py-1"
          value={layerData.x ?? 0}
          disabled={layer.locked}
          onChange={handleNumberChange("x")}
        />
      </div>
      <div className="flex flex-col">
        <label className="text-xs">Y</label>
        <input
          type="number"
          className="text-black rounded px-2 py-1"
          value={layerData.y ?? 0}
          disabled={layer.locked}
          onChange={handleNumberChange("y")}
        />
      </div>
      {layer.type === "text" && (
        <div className="flex flex-col">
          <label className="text-xs">Text</label>
          <input
            type="text"
            className="text-black rounded px-2 py-1"
            value={layerData.text ?? ""}
            disabled={layer.locked}
            onChange={handleTextChange}
          />
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs flex items-center">Font Size</label>
        <input
          type="number"
          className="text-black rounded px-2 py-1"
          value={layerData.fontSize ?? 16}
          disabled={layer.locked}
          onChange={handleFontSizeChange}
        />
        <label className="text-xs flex items-center">Rotation</label>
        <input
          type="number"
          className="text-black rounded px-2 py-1"
          value={layerData.rotation ?? 0}
          disabled={layer.locked}
          onChange={handleRotationChange}
        />
        <label className="text-xs flex items-center">Opacity</label>
        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={layerData.opacity ?? 1}
          disabled={layer.locked}
          onChange={handleOpacityChange}
        />
        <span className="text-xs text-right">{Math.round((layerData.opacity ?? 1) * 100)}%</span>
      </div>
      {layer.type === "text" && renderTypographyLayer ? renderTypographyLayer(layer) : null}
    </div>
  );
}

export function Inspector() {
  const layers = useMixboard((s) => s.layers);
  const update = useMixboard((s) => s.updateLayer);
  const setLayers = useMixboard((s) => s.setLayers);
  const autosave = useMixboard((s) => s.autosaving);
  const typographyAssets = useMixboard((s) => s.typographyAssets);
  const updateBackend = useMemo(
    () =>
      debounce(async (id: string, payload: Record<string, any>) => {
        try {
          await MixboardAPI.updateLayer(id, payload);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Layer sync failed", error);
        }
      }, 200),
    [],
  );

  const glyphMap = useMemo(() => {
    const map: Record<string, { char: string; url: string }> = {};
    (typographyAssets ?? []).forEach((asset: any) => {
      if (asset?.char) {
        map[asset.char] = asset;
        map[asset.char.toUpperCase()] = asset;
      }
    });
    return map;
  }, [typographyAssets]);

  const renderText = (str: string) => (
    <div className="flex flex-wrap gap-1">
      {str.split("").map((char, idx) => {
        const asset = glyphMap[char] || glyphMap[char.toUpperCase()];
        if (!asset) {
          return (
            <span key={`${char}-${idx}`} className="px-1 text-xs bg-muted rounded">
              {char}
            </span>
          );
        }
        return <img key={`${char}-${idx}`} src={asset.url} alt={char} className="h-8 w-auto" />;
      })}
    </div>
  );

  const RenderTypographyLayer = ({ layer }: { layer: any }) => {
    if (layer.type !== "text" || !layer.data?.text) return null;
    return <div className="mt-2 border-t border-border pt-2">{renderText(layer.data.text)}</div>;
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setLayers((prevLayers) => {
      const oldIndex = prevLayers.findIndex((layer) => layer.id === active.id);
      const newIndex = prevLayers.findIndex((layer) => layer.id === over.id);
      if (oldIndex === -1 || newIndex === -1) {
        return prevLayers;
      }
      const reordered = [...prevLayers];
      const [moved] = reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, moved);
      return reordered;
    });
  };

  return (
    <div className="w-80 bg-background/40 text-foreground p-4 border-l border-border">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">Layers</h2>
        {autosave && <span className="text-green-400 text-xs">Autosaving...</span>}
      </div>
      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={layers.map((layer) => layer.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-4">
            {layers.map((layer) => (
              <SortableLayer
                key={layer.id}
                layer={layer}
                update={update}
                renderTypographyLayer={RenderTypographyLayer}
                syncLayer={updateBackend}
                removeLayer={removeLayer}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
  const removeLayer = async (id: string) => {
    try {
      await MixboardAPI.deleteLayer(id);
      setLayers((prev) => prev.filter((layer) => layer.id !== id));
    } catch (error) {
      console.error("Failed to delete layer", error);
    }
  };
